import enUs from '../loc/en-us.json';
import frFr from '../loc/fr-fr.json';
import frCa from '../loc/fr-ca.json';
import esMx from '../loc/es-mx.json';

/**
 * Tracker #119 — Portal and Supplier Quality Workspace interface language.
 * Behaves exactly like SPFx (`src/services/LocalizationService.ts`): the same
 * four packs, the same names, the same nearest-pack rule, the same order —
 * the user's saved choice, else their Microsoft 365 language, else the
 * browser, else English. The saved choice is held on the platform and shared
 * with SPFx; the browser keeps only a copy for a fast first paint.
 */

export type InterfaceLanguage = 'en-us' | 'fr-fr' | 'fr-ca' | 'es-mx';

/** Shown by their own names, never a flag — the SPFx list. */
export const INTERFACE_LANGUAGES: ReadonlyArray<{ tag: InterfaceLanguage; nativeName: string }> = [
  { tag: 'en-us', nativeName: 'English' },
  { tag: 'fr-fr', nativeName: 'Français (France)' },
  { tag: 'fr-ca', nativeName: 'Français (Canada)' },
  { tag: 'es-mx', nativeName: 'Español (Latinoamérica)' }
];

export const DEFAULT_LANGUAGE: InterfaceLanguage = 'en-us';

export type PortalStrings = typeof enUs;
export type PortalStringKey = keyof PortalStrings;

const PACKS: Record<InterfaceLanguage, Record<string, string>> = {
  'en-us': enUs,
  'fr-fr': frFr,
  'fr-ca': frCa,
  'es-mx': esMx
};

/** The saved choice, as the platform returns it. */
export type LanguageChoice =
  | { mode: 'language'; language: InterfaceLanguage }
  | { mode: 'follow_microsoft_365'; language: null };

export const isInterfaceLanguage = (value: unknown): value is InterfaceLanguage =>
  typeof value === 'string' && INTERFACE_LANGUAGES.some((entry) => entry.tag === value);

/** The nearest pack, with the SPFx rule: es-* → es-mx; fr-ca → fr-ca, other fr-* → fr-fr; en-* → en-us. */
export const mapToInterfaceLanguage = (culture: string | null | undefined): InterfaceLanguage | null => {
  const tag = String(culture ?? '').trim().toLowerCase().replace('_', '-');
  if (!tag) return null;
  if (isInterfaceLanguage(tag)) return tag;
  const language = tag.split('-')[0];
  if (language === 'es') return 'es-mx';
  if (language === 'fr') return 'fr-fr';
  if (language === 'en') return 'en-us';
  return null;
};

/** Resolution order (design §4.1): saved choice → Microsoft 365 → browser → English. */
export const resolveInterfaceLanguage = (input: {
  choice: LanguageChoice | null;
  microsoft365Language?: string | null;
  browserLanguages?: ReadonlyArray<string>;
}): InterfaceLanguage => {
  if (input.choice?.mode === 'language' && isInterfaceLanguage(input.choice.language)) {
    return input.choice.language;
  }
  const microsoft365 = mapToInterfaceLanguage(input.microsoft365Language);
  if (microsoft365) return microsoft365;
  for (const browser of input.browserLanguages ?? []) {
    const mapped = mapToInterfaceLanguage(browser);
    if (mapped) return mapped;
  }
  return DEFAULT_LANGUAGE;
};

export type Translate = (key: PortalStringKey, tokens?: Record<string, string | number>) => string;

/** `t(key, tokens)` with `{{token}}` substitution (the SPFx syntax); falls back to English, then the key. */
export const createTranslator = (language: InterfaceLanguage): Translate => {
  const pack = PACKS[language] ?? PACKS[DEFAULT_LANGUAGE];
  return (key, tokens) => {
    const template = pack[key] ?? PACKS[DEFAULT_LANGUAGE][key] ?? key;
    return tokens
      ? template.replace(/\{\{(.*?)\}\}/g, (match, name: string) => {
          const value = tokens[name.trim()];
          return value === undefined ? match : String(value);
        })
      : template;
  };
};

// ---------------------------------------------------------------------------
// The browser copy: only a copy of the platform's value, for a fast first
// paint; overwritten from the platform on every load, never sent back.
// ---------------------------------------------------------------------------

export const BROWSER_COPY_KEY = 'EDIN_PORTAL_LANGUAGE_COPY';

export const readBrowserCopy = (storage: Pick<Storage, 'getItem'> | undefined): LanguageChoice | null => {
  try {
    const parsed = JSON.parse(storage?.getItem(BROWSER_COPY_KEY) ?? 'null') as Partial<LanguageChoice> | null;
    if (parsed?.mode === 'follow_microsoft_365') return { mode: 'follow_microsoft_365', language: null };
    if (parsed?.mode === 'language' && isInterfaceLanguage(parsed.language)) return { mode: 'language', language: parsed.language };
  } catch {
    // An unreadable copy is ignored; the platform's value replaces it.
  }
  return null;
};

export const writeBrowserCopy = (storage: Pick<Storage, 'setItem' | 'removeItem'> | undefined, choice: LanguageChoice | null): void => {
  try {
    if (choice) storage?.setItem(BROWSER_COPY_KEY, JSON.stringify(choice));
    else storage?.removeItem(BROWSER_COPY_KEY);
  } catch {
    // Storage may be unavailable (private mode); the copy is only an optimisation.
  }
};

/** Reads `{ preference, microsoft365Language }` from the language endpoint. */
export const parseLanguageSetting = (body: unknown): { choice: LanguageChoice | null; microsoft365Language: string | null } => {
  const record = (body && typeof body === 'object' ? body : {}) as { preference?: Partial<LanguageChoice> | null; microsoft365Language?: unknown };
  const preference = record.preference;
  const choice: LanguageChoice | null = preference?.mode === 'follow_microsoft_365'
    ? { mode: 'follow_microsoft_365', language: null }
    : preference?.mode === 'language' && isInterfaceLanguage(preference.language)
      ? { mode: 'language', language: preference.language }
      : null;
  return { choice, microsoft365Language: typeof record.microsoft365Language === 'string' ? record.microsoft365Language : null };
};
